import type { CoachingPartnership, Resource } from '@efdigital/ll-addon-data';
import { titleCase } from '@efdigital/ll-addon-ui';
import { EmptyState } from '@efdigital/ll-xceed-v2/components/application/empty-state';
import ResourceThumbnail from '@efdigital/ll-xceed-v2/components/coaching/resource-thumbnail.gts';
import ShareResourceModal from '@efdigital/ll-xceed-v2/components/coaching/share-resource-modal.gts';
import config from '@efdigital/ll-xceed-v2/config/environment';
import { deleteCoachingSharedResource } from '@efdigital/ll-xceed-v2/data/builders/coaching/coaching-shared-resources';
import type {
  CoachingSharedResource,
  SharedResourceItem,
} from '@efdigital/ll-xceed-v2/data/types/coaching-shared-resource';
import { leadershipLiveResourceUrl } from '@efdigital/ll-xceed-v2/utils/coaching/leadership-live-resource-url';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';

const LLIVE_ORIGIN = config['ll-apps'].llive;

interface SharedResourcesListSignature {
  Args: {
    items: readonly SharedResourceItem[];
    /** Partnership new shares hang off. Required to share; omitted hides the catalog. */
    partnership?: CoachingPartnership | null;
    role: 'coach' | 'coachee';
  };
  Element: HTMLDivElement;
}

function openUrl(resource: Resource): string | null {
  return leadershipLiveResourceUrl(LLIVE_ORIGIN, resource);
}

function formatSharedAt(iso: string | undefined): string | null {
  if (!iso) {
    return null;
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Shared list of resources a coach has sent to a coachee.
 *
 * Opening always leaves Xceed for Leadership Live. The coach shares from the
 * library and can unshare; the coachee only opens items.
 */
export default class SharedResourcesList extends Component<SharedResourcesListSignature> {
  @service declare store: Store;
  @service declare router: RouterService;

  @tracked confirmingId: string | null = null;
  @tracked saving = false;
  @tracked errorMessage: string | null = null;

  get canShare(): boolean {
    return this.args.role === 'coach' && Boolean(this.args.partnership);
  }

  get canRemove(): boolean {
    return this.args.role === 'coach';
  }

  get sharePartnership(): CoachingPartnership | null {
    return this.args.partnership ?? null;
  }

  get sharedResourceIds(): string[] {
    return this.args.items
      .map((item) => item.resource?.id)
      .filter((id): id is string => Boolean(id));
  }

  get emptyMessage(): string {
    return this.args.role === 'coach'
      ? 'Nothing on this list yet.'
      : 'Nothing here yet. When your coach sends you a resource, it will show up here.';
  }

  get missingResourceDetail(): string {
    return this.canRemove
      ? 'This item was removed from the catalog. You can still take it off this list.'
      : 'This item was removed from the catalog.';
  }

  isConfirming = (share: CoachingSharedResource): boolean =>
    this.confirmingId === share.id;

  refresh = async () => {
    await this.router.refresh();
  };

  startConfirm = (share: CoachingSharedResource) => {
    this.confirmingId = share.id;
    this.errorMessage = null;
  };

  cancelConfirm = () => {
    this.confirmingId = null;
  };

  remove = async (share: CoachingSharedResource) => {
    if (this.saving) {
      return;
    }

    this.saving = true;
    this.errorMessage = null;

    try {
      await this.store.request(deleteCoachingSharedResource(share));
      this.confirmingId = null;
      await this.router.refresh();
    } catch (error) {
      console.error(error);
      this.errorMessage = 'That item could not be removed. Please try again.';
    } finally {
      this.saving = false;
    }
  };

  <template>
    <div ...attributes>
      {{#if this.canShare}}
        <ShareResourceModal
          @partnership={{this.sharePartnership}}
          @sharedResourceIds={{this.sharedResourceIds}}
          @onShared={{this.refresh}}
        >
          <:default as |showModal|>
            <div
              class="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4"
            >
              <p class="text-body-secondary mb-0">Send resources from the
                library.</p>
              <button
                type="button"
                class="btn btn-primary d-inline-flex align-items-center gap-1"
                data-test-share-resource-open
                {{on "click" showModal}}
              >
                <iconify-icon icon="ph:plus-bold" inline aria-hidden="true" />
                Share from library
              </button>
            </div>
          </:default>
        </ShareResourceModal>
      {{/if}}

      {{#if this.errorMessage}}
        <p class="text-danger small" role="alert">{{this.errorMessage}}</p>
      {{/if}}

      {{#if @items.length}}
        <ul class="list-unstyled d-flex flex-column gap-3 mb-0">
          {{#each @items as |item|}}
            <li
              class="card border shadow-sm rounded-4 overflow-hidden"
              data-test-shared-resource-card
            >
              <div class="row g-0">
                <div class="col-lg-4 d-flex">
                  <ResourceThumbnail
                    @resource={{item.resource}}
                    @stretch={{true}}
                    @preferFullImage={{true}}
                    class="w-100"
                  />
                </div>
                <div class="col-lg-8">
                  <div class="card-body p-4">
                    <div
                      class="d-flex flex-wrap align-items-start justify-content-between gap-3"
                    >
                      <div class="min-w-0">
                        {{#if item.resource}}
                          <h3 class="h5 fw-bold mb-2">{{titleCase
                              item.resource.title
                            }}</h3>
                          {{#if item.resource.abstract}}
                            <p
                              class="mb-2 max-lines-4"
                            >{{item.resource.abstract}}</p>
                          {{/if}}
                        {{else}}
                          <h3 class="h5 fw-bold mb-2">No longer in the library</h3>
                          <p
                            class="text-body-secondary mb-2"
                          >{{this.missingResourceDetail}}</p>
                        {{/if}}
                        {{#if item.share.note}}
                          <div
                            class="border-start border-primary border-3 ps-3 py-1 mb-2"
                          >
                            <p class="mb-0 max-lines-3">{{item.share.note}}</p>
                          </div>
                        {{/if}}
                        {{#let (formatSharedAt item.share.createdAt) as |when|}}
                          {{#if when}}
                            <p class="small text-body-secondary mb-0">Shared
                              {{when}}</p>
                          {{/if}}
                        {{/let}}
                      </div>
                      <div class="d-flex flex-wrap gap-2">
                        {{#if item.resource}}
                          {{#let (openUrl item.resource) as |url|}}
                            {{#if url}}
                              <a
                                href={{url}}
                                class="btn btn-outline-primary d-inline-flex align-items-center gap-1"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-test-open-resource
                              >
                                Open
                                <span class="visually-hidden">
                                  {{item.resource.title}}</span>
                                <iconify-icon
                                  icon="ph:arrow-square-out"
                                  inline
                                  aria-hidden="true"
                                />
                                <span class="visually-hidden">
                                  (opens in a new tab)
                                </span>
                              </a>
                            {{/if}}
                          {{/let}}
                        {{/if}}
                        {{#if this.canRemove}}
                          {{#if (this.isConfirming item.share)}}
                            <button
                              type="button"
                              class="btn btn-primary"
                              disabled={{this.saving}}
                              data-test-confirm-remove-share
                              {{on "click" (fn this.remove item.share)}}
                            >{{if
                                this.saving
                                "Removing…"
                                "Yes, remove"
                              }}</button>
                            <button
                              type="button"
                              class="btn btn-outline-primary"
                              {{on "click" this.cancelConfirm}}
                            >Keep</button>
                          {{else}}
                            <button
                              type="button"
                              class="btn btn-outline-primary"
                              data-test-remove-share
                              {{on "click" (fn this.startConfirm item.share)}}
                            >Remove</button>
                          {{/if}}
                        {{/if}}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </li>
          {{/each}}
        </ul>
      {{else}}
        <EmptyState @message={{this.emptyMessage}} @icon="ph:books" />
      {{/if}}
    </div>
  </template>
}
